import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, Building, Shield, Users, FileText, ArrowRight, CheckCircle, AlertTriangle } from 'lucide-react';
import { Link } from 'react-router-dom';
import Navbar from '../components/Navbar';
import SEOHead from '../components/SEOHead';
import Footer from '../components/Footer';
import UnifiedContactModal from '../components/UnifiedContactModal';

const MunicipalFloodResilientIntakesPage = () => {
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="Flood-Resilient Intakes & Pump Stations for Municipal & District Systems | CTC Waterworks"
        description="Self-cleaning intake screens and filtration for municipal water, wastewater, stormwater, and irrigation/diking districts in flood-prone areas. Support for flood recovery and long-term resilience."
        canonicalPath="/municipal/flood-resilient-intakes"
      />
      <Navbar />

      <section className="pt-24 pb-16 bg-gradient-to-br from-blue-900 via-cyan-900 to-teal-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYtMi42ODYgNi02cy0yLjY4Ni02LTYtNi02IDIuNjg2LTYgNiAyLjY4NiA2IDYgNnptMCAzNmMzLjMxNCAwIDYtMi42ODYgNi02cy0yLjY4Ni02LTYtNi02IDIuNjg2LTYgNiAyLjY4NiA2IDYgNnpNNTQgMzZjMy4zMTQgMCA2LTIuNjg2IDYtNnMtMi42ODYtNi02LTYtNiAyLjY4Ni02IDYgMi42ODYgNiA2IDZ6TTYgMzZjMy4zMTQgMCA2LTIuNjg2IDYtNnMtMi42ODYtNi02LTYtNiAyLjY4Ni02IDYgMi42ODYgNiA2IDZ6IiBzdHJva2U9IiNmZmYiIHN0cm9rZS13aWR0aD0iMiIvPjwvZz48L3N2Zz4=')] opacity-5"></div>
        </div>

        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center space-x-2 bg-cyan-600/20 text-cyan-300 px-4 py-2 rounded-full text-sm font-medium mb-6 border border-cyan-500/30">
              <Building size={16} />
              <span>Municipal & District Systems</span>
            </div>
            <h1 className="text-5xl lg:text-6xl font-bold mb-6">
              Flood-Resilient Intakes & Pump Stations
            </h1>
            <p className="text-xl text-gray-300 max-w-4xl mx-auto leading-relaxed">
              Self-cleaning intake screens and engineered filtration for raw water, stormwater, and district pump stations facing higher debris and dirty water after major flood events.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="flex flex-col sm:flex-row gap-4 justify-center"
          >
            <button
              onClick={() => setIsContactModalOpen(true)}
              className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-cyan-700 hover:to-blue-700 transition-all duration-300 shadow-lg inline-flex items-center justify-center space-x-3"
            >
              <span>Talk to Our Engineering Team</span>
              <ArrowRight size={20} />
            </button>
            <a
              href="tel:+15099017476"
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-blue-900 transition-all duration-300 inline-flex items-center justify-center space-x-2"
            >
              <Phone size={20} />
              <span>Call Now (509) 901-7476</span>
            </a>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              What Historic Floods Mean for Intakes, Screens & Pump Stations
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 leading-relaxed space-y-4">
              <p>
                For municipal utilities, irrigation districts, and diking/drainage districts, major floods and atmospheric river events are more than a one-week emergency. They:
              </p>
              <ul className="space-y-3 my-6">
                {[
                  'Reshape riverbeds and canals, changing sediment and debris patterns',
                  'Undercut and damage intake structures, culverts, and headwalls',
                  'Load channels with driftwood, trash, and fine sediment',
                  'Increase organics and nutrients that drive algae and biofilm in basins and sumps'
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <AlertTriangle className="text-orange-600 flex-shrink-0 mt-1" size={20} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xl font-semibold text-gray-900">
                The next time flows come up—whether it's winter storms or spring snowmelt—your intakes, screens, and pump stations will be dealing with a different world than before the flood.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
              Self-Cleaning Intake Protection for Raw Water, Stormwater & District Pumps
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-8 border border-blue-200">
                <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-cyan-600 rounded-lg flex items-center justify-center mb-6">
                  <Shield className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Raw Water & Non-Potable Intakes</h3>
                <ul className="space-y-3">
                  {[
                    'River and canal intakes feeding treatment plants or non-potable distribution',
                    'Self-cleaning intake screens to keep trash, branches, and large debris out of pumps and downstream structures',
                    'Designs focused on safe, reliable performance under high-debris, high-velocity events'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="text-blue-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-xl p-8 border border-cyan-200">
                <div className="w-16 h-16 bg-gradient-to-br from-cyan-600 to-teal-600 rounded-lg flex items-center justify-center mb-6">
                  <Building className="text-white" size={32} />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">Stormwater, Drainage & District Pump Stations</h3>
                <ul className="space-y-3">
                  {[
                    'Stormwater pump stations in low-lying areas',
                    'Irrigation, drainage, and diking district pump stations on canals and drainages',
                    'Self-cleaning screens on culvert inlets or pump suctions to reduce manual raking and plugging'
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <CheckCircle className="text-cyan-600 flex-shrink-0 mt-0.5" size={18} />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            <div className="mt-8 bg-gradient-to-br from-slate-50 to-gray-100 rounded-xl p-8 border border-gray-200">
              <p className="text-lg text-gray-700 leading-relaxed text-center">
                Our role is focused and specific: we design and manufacture self-cleaning intake screens and filtration systems for "ugly" water. That includes municipal, industrial, and district applications where debris and sediment loads are increasing after major events.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
              Why Self-Cleaning Intakes Matter in Flood Years
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: Shield,
                  title: 'Reduce Manual Cleaning',
                  description: 'Automatically shed debris from screen surfaces, cutting down on raking and high-frequency site visits during storms.'
                },
                {
                  icon: AlertTriangle,
                  title: 'Maintain Flow Under Debris Load',
                  description: 'Maintain more consistent pump suction conditions, reducing nuisance trips and loss of capacity when you need it most.'
                },
                {
                  icon: Users,
                  title: 'Improve Safety & Resilience',
                  description: 'Keep crews out of dangerous, fast-moving water and unstable banks as much as possible, while supporting long-term resilience goals.'
                }
              ].map((benefit, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="bg-white rounded-xl p-8 border-2 border-gray-200 shadow-lg"
                >
                  <div className="w-16 h-16 bg-gradient-to-br from-cyan-600 to-blue-600 rounded-full flex items-center justify-center mb-6">
                    <benefit.icon className="text-white" size={28} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                  <p className="text-gray-700">{benefit.description}</p>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 bg-gradient-to-r from-blue-600 to-cyan-600 rounded-xl p-8 text-white">
              <p className="text-lg leading-relaxed text-center">
                In many cases, self-cleaning intake screens can be integrated into repair or replacement projects you're already planning as part of flood recovery, rather than stand-alone upgrades.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Designed to Fit Into Flood Recovery & Resilience Programs
            </h2>
            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-xl p-8 border border-blue-200">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Many owners are using major flood events as an opportunity to rebuild intakes and pump stations in a more resilient way. While every project is different, self-cleaning intake screens and robust filtration:
              </p>
              <ul className="space-y-3">
                {[
                  'Can be considered as hazard-mitigation or resilience measures in permanent repair projects',
                  'Often align with the goals of federal and state programs that support "building back better" after disasters',
                  'Help demonstrate reduced future risk and lower O&M burden for staff'
                ].map((item, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <CheckCircle className="text-blue-600 flex-shrink-0 mt-1" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </li>
                ))}
              </ul>
              <p className="text-lg text-gray-700 mt-6 leading-relaxed">
                We don't administer funding programs, but we support owners and their engineers with the technical information they need to evaluate where our equipment fits within their funded projects.
              </p>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">
              Technical Support for Owners & Consulting Engineers
            </h2>
            <div className="bg-gradient-to-br from-slate-50 to-white rounded-xl p-8 border border-gray-200 shadow-lg">
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Your design and procurement process is already complex. Our goal is to make self-cleaning intakes and filtration easy to evaluate and specify, not another headache.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-4">We provide:</h3>
                  <ul className="space-y-3">
                    {[
                      'Preliminary sizing and concept layouts for proposed intakes and pump stations',
                      'Performance data and design criteria (approach velocities, headloss, materials, clearances)',
                      'Sample specification language for "Self-Cleaning Intake Screens" sections',
                      'Coordination with your in-house or consulting engineers from concept through submittals'
                    ].map((item, index) => (
                      <li key={index} className="flex items-start space-x-2">
                        <CheckCircle className="text-ctc-blue flex-shrink-0 mt-0.5" size={18} />
                        <span className="text-gray-700">{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-lg p-6 border border-blue-200">
                  <FileText className="text-blue-600 mb-4" size={40} />
                  <h4 className="text-lg font-bold text-gray-900 mb-3">Request Technical Information</h4>
                  <p className="text-gray-700 mb-4">
                    Need preliminary sizing, design criteria, or sample spec language for your project?
                  </p>
                  <button
                    onClick={() => setIsContactModalOpen(true)}
                    className="inline-flex items-center space-x-2 text-blue-700 font-semibold hover:text-blue-800 transition-colors"
                  >
                    <span>Request technical brief & spec language</span>
                    <ArrowRight size={18} />
                  </button>
                </div>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mb-16"
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-8 text-center">
              Public Works, Utilities & Districts We Can Help
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                'City and town water and wastewater utilities',
                'County public works and stormwater divisions',
                'Irrigation, drainage, and diking districts',
                'Special purpose districts with canal, river, or lake intakes',
                'Consulting engineers designing pump stations and headworks',
                'Regional water authorities and joint districts'
              ].map((org, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: index * 0.05 }}
                  className="bg-gradient-to-br from-cyan-50 to-blue-50 rounded-lg p-6 border border-cyan-200 flex items-center space-x-3"
                >
                  <CheckCircle className="text-cyan-600 flex-shrink-0" size={24} />
                  <span className="text-gray-900 font-medium">{org}</span>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 bg-white rounded-xl p-8 border-2 border-gray-200 shadow-lg">
              <p className="text-lg text-gray-700 leading-relaxed text-center">
                If you are planning repairs, upgrades, or new construction related to intakes, pump stations, or headworks in the wake of recent flood events, a short conversation can clarify whether self-cleaning intake screens or filtration systems are a good fit.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-800 via-slate-900 to-black text-white">
        <div className="max-w-4xl mx-auto px-6">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h2 className="text-4xl font-bold mb-6">
              Discuss Flood-Resilient Intake Options With Our Team
            </h2>
            <p className="text-xl text-gray-300 mb-8 leading-relaxed">
              We regularly work alongside municipal staff and consulting engineers to help evaluate intake and filtration options for flood-prone sites.
            </p>
            <p className="text-lg text-gray-400 mb-8">
              Share a few details about your site and we'll follow up quickly to see if CTC equipment is a good fit and what information you might need.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
              <a
                href="tel:+15099017476"
                className="bg-gradient-to-r from-cyan-600 to-blue-600 text-white px-8 py-4 rounded-lg font-semibold hover:from-cyan-700 hover:to-blue-700 transition-all duration-300 shadow-lg inline-flex items-center justify-center space-x-3"
              >
                <Phone size={20} />
                <span>Call Now (509) 901-7476</span>
              </a>
              <button
                onClick={() => setIsContactModalOpen(true)}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-slate-900 transition-all duration-300 inline-flex items-center justify-center space-x-2"
              >
                <span>Request a Call Back</span>
                <ArrowRight size={20} />
              </button>
            </div>

            <p className="text-sm text-gray-500">
              We prioritize flood and dirty-water intake questions and typically respond within one business day. If you prefer, call us directly at (509) 901-7476.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />

      <UnifiedContactModal
        isOpen={isContactModalOpen}
        onClose={() => setIsContactModalOpen(false)}
        productType="filtration"
        formSource="municipal_flood_intakes_page"
      />
    </div>
  );
};

export default MunicipalFloodResilientIntakesPage;
